import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Eye, EyeOff } from "lucide-react";
import logo from "../assets/logo.png";
import { useAuth } from "../context/AuthContext";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    role: "",
    email: "",
    password: "",
  });
  const [formData, setFormData] = useState({
    role: "",
    email: "",
    password: "",
  });

  const roles = [
    { value: "admin", label: "Admin" },
    { value: "subAdmin", label: "Sub Admin" },
    { value: "staff", label: "Staff" },
    { value: "credit-manager", label: "Credit Manager" },
    { value: "banking-partner", label: "Banking Partner" },
    { value: "affiliate-partner", label: "Affiliate Partner" },
  ];
  const getRoleSpecificContent = () => {
    switch (formData.role) {
      case "admin":
        return {
          title: "Admin Control Panel",
          description:
            "Access your administrative tools and manage all company operations. Full system control and oversight.",
        };
      case "subAdmin":
        return {
          title: "Sub-Admin Control Panel",
          description:
            "Access delegated administrative tools and assist in managing company operations.",
        };
      case "staff":
        return {
          title: "Staff Portal",
          description:
            "Access your assigned tasks and manage daily operations. Track and update customer information.",
        };
      case "credit-manager":
        return {
          title: "Credit Management Portal",
          description:
            "Manage credit assessments, loan approvals, and risk analysis. Monitor credit portfolios.",
        };
      case "banking-partner":
        return {
          title: "Banking Partner Portal",
          description:
            "Access loan management tools and banking integration features. Monitor transactions and settlements.",
        };
      case "affiliate-partner":
        return {
          title: "Affiliate Partner Portal",
          description:
            "Track referrals, manage commissions, and access partner resources.",
        };
      default:
        return {
          title: "Admin Control Panel",
          description:
            "Access your administrative tools and manage company operations. Secure access for authorized personnel only.",
        };
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({
      role: "",
      email: "",
      password: "",
    });

    try {
      const response = await login(formData);
      console.log("Login response:", response);
      if (response.success && response.token) {
        localStorage.setItem("token", response.token);

        // toast.success("Login successful!");
        const userRole = response.user.role;
        switch (userRole) {
          case "admin":
            // toast.success("Login successful");
            navigate(
              "/admin/dashboard",
              { replace: true },
              { state: { toasterr: true } }
            );
            break;
          case "subAdmin":
            navigate("/subAdmin/dashboard", { replace: true });
            break;
          case "staff":
            navigate("/staff/dashboard", { replace: true });
            break;
          case "credit-manager":
            navigate("/credit-manager/dashboard", { replace: true });
            break;
          case "banking-partner":
            navigate("/banking-partner/dashboard", { replace: true });
            break;
          case "affiliate-partner":
            navigate("/affiliate-partner/dashboard", { replace: true });
            break;
          default:
            navigate("/", { replace: true });
        }
      } else {
        throw new Error("Login response is invalid");
      }
    } catch (error) {
      // Handle specific error messages from backend
      console.error("Login failed:", error.response, error);
      // toast.error('Login failed. Please try again.');
      if (error.success === false) {
        // Handle specific error type
        if (error.errorType === "invalid_role") {
          setErrors((prev) => ({
            ...prev,
            role: error.message,
          }));
        }
        if (error.errorType === "invalid_email") {
          setErrors((prev) => ({
            ...prev,
            email: error.message,
          }));
        }
        if (error.errorType === "invalid_password") {
          setErrors((prev) => ({
            ...prev,
            password: error.message,
          }));
        }
        if (error.errorType === "not_approved") {
          toast.error(error.message);
        }
        if (error.errorType === "affiliate_not_found") {
          toast.error(error.message);
        }
        if (error.errorType === "affiliate_not_active") {
          toast.error(error.message);
        }
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };
  const { title, description } = getRoleSpecificContent();
  return (
    <div className="min-h-screen flex bg-black">
      {/* Left Section - Logo and Description */}
      <div className="hidden md:flex md:w-1/2 flex-col justify-center items-center p-16">
        <img
          src={logo}
          alt="LONO"
          className="mb-4"
          style={{ width: "50%", height: "50%" }}
        />
        <h1 className="text-2xl font-bold text-white mb-4">{title}</h1>
        <p className="text-gray-400 text-sm max-w-md text-center">
          {description}
        </p>
      </div>

      {/* Right Section - Login Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-4">
        <div className="bg-[#131313] p-8 rounded-lg shadow-lg w-full max-w-md">
          <h2 className="text-xl font-semibold text-white mb-6">
            Login To LONOOOOO
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            Choose your role and enter your credentials to access the respective
            portal
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Select Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className={`w-full p-3 bg-[#131313] border ${
                  errors.role ? "border-red-500" : "border-[#333333]"
                } rounded text-white focus:outline-none focus:border-[#FFCA20]`}
                required
                disabled={isLoading}
              >
                <option value="">Select Your Role</option>
                {roles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="text-red-500 text-sm mt-1">{errors.role}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                placeholder="Enter Your Email Address"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full p-3 bg-[#131313] border ${
                  errors.email ? "border-red-500" : "border-[#333333]"
                } rounded text-white focus:outline-none focus:border-[#FFCA20]`}
                required
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter Your Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full p-3 bg-[#131313] border ${
                    errors.password ? "border-red-500" : "border-[#333333]"
                  } rounded text-white focus:outline-none focus:border-[#FFCA20] pr-10`}
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="appearance-none h-5 w-5 border-2 border-[#333333] rounded-sm bg-transparent checked:bg-transparent checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                    before:content-['✓'] before:absolute before:text-[#ffca20] before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                  disabled={isLoading}
                />
                <span className="ml-2 text-sm text-gray-400">Remember me</span>
              </label>
              <p className="text-sm text-[#FFCA20] hover:underline hover:cursor-pointer">
                Forgot Password?
              </p>
            </div>

            <button
              type="submit"
              className={`w-full bg-[#FFCA20] text-black font-semibold py-3 rounded hover:bg-[#E5B61D] transition duration-300 ${
                isLoading ? "opacity-70 cursor-not-allowed" : ""
              }`}
              disabled={isLoading}
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </button>

            <div className="text-center text-sm text-gray-400 mt-4">
              Need Help?{" "}
              <span className="text-[#FFCA20] hover:underline hover:cursor-pointer">
                Contact Support
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
